import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, FormGroup, Input, Row } from "reactstrap";
import { useParams } from "react-router-dom";

import TabPanel from "components/TabPanel";
import TabView from "components/TabView";
import { validate } from "components/validateGeneralForm";
import Dados from "./Dados";
import Items from "./Items";

import TitleFormWithEdit from "components/TitleFormWithEdit";
import Movimentacoes from "./Movimentacoes";
import Execucoes from "./Execucoes";
import Correcoes from "./Correcoes";
import Fiscalizacao from "./Fiscalizacao";
import CustomDropzone from "components/CustomDropzone";
import DetalhesEmpresa from "components/DetalhesEmpresa";
import ordemServicoService from "services/OrdemServicoService";
import { getStatusOrdemServicoDescription } from "services/OrdemServicoService";
import AppContext from "AppContext";
import { PapelEnum } from "services/PerfilService";
import { StatusOrdemServicoEnum } from "services/OrdemServicoService";
import { TipoContratoEnum, getTipoContratoDescription } from "services/ContratoService";
import { totalizar } from "services/Utils";
import Prioridade from "components/Prioridade";

const statusPermitidosEdicaoNormal = [StatusOrdemServicoEnum.PENDENTE, StatusOrdemServicoEnum.AGUARDANDO_SELECAO_EMPRESA_EXECUTORA, StatusOrdemServicoEnum.EM_FASE_EXECUCAO]
const statusPermitidosEdicaoSemaforico = [StatusOrdemServicoEnum.PENDENTE]
const statusPermitidosFiscalizacao = [StatusOrdemServicoEnum.AGUARDANDO_FISCALIZACAO, StatusOrdemServicoEnum.EM_FISCALIZACAO]

export default function FormOrdemServico() {
  const [data, setData] = useState()
  const [edit, setEdit] = useState(false)
  const [itens, setItens] = useState()
  const { state } = useContext(AppContext)
  const params = useParams()

  const id = parseInt(params.id)

  const acoesUsuario = state?.user?.acoes || []
  const idTipoPerfil = state?.user?.perfil?.idTipo

  const podeEditar = useMemo(() => {
    if (!data)
      return false

    if (!acoesUsuario.includes('os.gerenciar_item'))
      return false

    if (data.contrato_tipoContrato == TipoContratoEnum.NORMAL)
      return (idTipoPerfil == PapelEnum.FISCAL || idTipoPerfil == PapelEnum.ADMIN || idTipoPerfil == PapelEnum.GESTOR_CONTRATO) && statusPermitidosEdicaoNormal.includes(data.idStatus)

    if (data.contrato_tipoContrato == TipoContratoEnum.SEMAFORICO)
      return idTipoPerfil == PapelEnum.GESTOR_EMPRESA && statusPermitidosEdicaoSemaforico.includes(data.idStatus)

  }, [data])

  const mostrarAbaFiscalizacao = useMemo(() => {
    if (!data)
      return false

    if (!statusPermitidosFiscalizacao.includes(data.idStatus))
      return false

    if (!acoesUsuario.includes('os.iniciar_fiscalizacao'))
      return false

    return (data.contrato_lote_idsFiscais || []).some(idFiscal => idFiscal == state.user.id) || data.contrato_idGestor == state.user.id
  }, [data])

  const valorTotalOs = useMemo(() => {
    if (!itens?.length) {
      return 0
    }

    return totalizar(itens.map(el => el.precoTotal))
  }, [itens])

  const dadosForm = useMemo(() => {
    if (!data) {
      return data
    }

    return {
      ...data,
      contrato_tipoContratoFmt: getTipoContratoDescription(data.contrato_tipoContrato),
      valorTotalOs
    }
  }, [data, valorTotalOs])

  const carregar = () => {
    ordemServicoService.getById(id).then(res => {
      if (res.success) {
        setData(res.data)
      } else {
        setData(null)
      }
    })
  }

  const carregarItens = () => {
    ordemServicoService.getItens(id).then(res => {
      if (res.success) {
        setItens(res.data)
      }
    })
  }

  useEffect(() => {
    carregar()
    carregarItens()
  }, [id])

  const formConfig = useMemo(() => {

    if (!data)
      return []

    const camposAdicionais = []

    if (data.temEmpresaExecutora) {
      camposAdicionais.push({ name: "titulo_empresa", type: "view", disabled: true, colspan: 12, component: () => <h2 className="mt-3">Empresa Executora</h2> })
      camposAdicionais.push({ name: "empresaExecutora_razaoSocial", label: 'Razão Social', type: "text", disabled: true, colspan: 12 })
      camposAdicionais.push({ name: 'viewDetalhesEmpresaExecutora', type: 'view', component: () => <DetalhesEmpresa idEmpresa={data.empresaExecutora_id} /> })
    }

    return [
      [
        { name: "us", label: 'US', type: "text", colspan: 2, disabled: true },
        { name: "local", label: 'Local', type: "text", colspan: 4, disabled: true },
        { name: "prazoExecucaoFmt", label: 'Prazo Execução', type: "text", colspan: 2, disabled: true },
        { name: "dataPrevisaoInicioFmt", label: 'Previsão Início', type: "text", colspan: 2, disabled: true },
        {
          // SDD 037 - prioridade somente leitura (vazia = "Sem prioridade")
          name: "prioridade",
          type: "view",
          colspan: 2,
          component: () => (
            <FormGroup>
              <label>Prioridade</label>
              <div className="form-control d-flex align-items-center" style={{ background: '#e9ecef' }}>
                <Prioridade
                  prioridade={data.prioridade}
                  concluida={[StatusOrdemServicoEnum.CONCLUIDA, StatusOrdemServicoEnum.CANCELADA].includes(data.idStatus)}
                />
              </div>
            </FormGroup>
          )
        },
        { name: "contrato_tipoContratoFmt", label: 'Tipo de OS', type: "text", disabled: true, colspan: 3 },
        { name: "valorTotalOs", label: 'Valor da OS', type: "decimal", disabled: true, colspan: 3 },
        { name: "dataInicioFmt", label: 'Data Início Real', type: "text", disabled: true, colspan: 3 },
        { name: "dataConclusaoFmt", label: 'Data Final Real', type: "text", disabled: true, colspan: 3 },
        {
          name: "descricao",
          label: 'Descrição',
          type: "view",
          colspan: 12,
          hide: !data.descricao_ocorrencia,
          component: () => (
            <div>
              <h2 className="mt-3">Descrição</h2>
              <pre>{data.descricao_ocorrencia}</pre>
            </div>
          )
        },
        { name: "titulo_projetos", type: "view", disabled: true, colspan: 12, component: () => <h2 className="mt-3">Projeto</h2>, hide: data.contrato_tipoContrato == TipoContratoEnum.SEMAFORICO },
        { name: "projeto_numenc", label: 'Numenc', type: "text", colspan: 2, disabled: true, hide: data.contrato_tipoContrato == TipoContratoEnum.SEMAFORICO },
        { name: "projeto_titulo", label: 'Título', type: "text", colspan: 8, disabled: true, hide: data.contrato_tipoContrato == TipoContratoEnum.SEMAFORICO },
        {
          name: "projeto_arquivo",
          type: "view",
          required: true,
          colspan: 6,
          hide: data.contrato_tipoContrato == TipoContratoEnum.SEMAFORICO,
          component: () => {
            return (
              <>
                <div>Arquivo</div>
                <div>
                  {data.projeto_nomeArquivo ? (
                    <a href={`${process.env.REACT_APP_API_URL}arquivo/${data.projeto_tokenArquivo}/download`}>{data.projeto_nomeArquivo || ''}</a>
                  ) : (
                    <div>Não existe arquivo relacionado a projeto</div>
                  )}
                </div>
              </>
            )
          }
        },

        { name: "titulo_contrato", type: "view", disabled: true, colspan: 12, component: () => <h2 className="mt-5">Contrato</h2> },
        { name: "contrato_numero", label: 'Nº Contrato', type: "text", disabled: true, colspan: 3 },
        { name: "contrato_lote", label: 'Lote', type: "text", disabled: true, colspan: 3 },
        { name: "contrato_fiscal", label: 'Fiscal', type: "text", disabled: true, colspan: 12 },

        ...camposAdicionais
      ],
    ]
  }, [data, edit])

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value })
  }

  const handleChangMovimentacoes = () => {
    carregar()
    carregarItens()
  }

  const handleSubmit = e => {
    e.preventDefault()

    const errors = validate(formConfig, data)
    if (errors.length > 0) {
      setData({ ...data, errors })
      // erros foram encontrados
      return
    }
  }

  if (!data)
    return null

  return (
    <Container fluid>
      <TitleFormWithEdit title="Ordens de Serviço" edit={edit} onChangeEdit={setEdit} showButton={podeEditar} />
      <Row>
        <FormGroup className="col-2">
          <label>OS</label>
          <Input type="text" value={data.numeroFmt} disabled={true} />
        </FormGroup>
        <FormGroup className="col-6">
        </FormGroup>
        <FormGroup className="col-4">
          <label>Status</label>
          <Input type="text" value={data.status} disabled={true} />
        </FormGroup>
      </Row>
      <TabView>
        <TabPanel label="Dados">
          <form onSubmit={handleSubmit}>
            <Dados data={dadosForm} onChange={handleChange} formConfig={formConfig} edit={edit} />
            {/* {edit && (
              <div className="text-right">
                <Button type="submit" color="primary">Salvar</Button>
              </div>
            )} */}
          </form>
        </TabPanel>
        <TabPanel label="Itens">
          <Items
            idContrato={data.contrato_id}
            idOrdemServico={id}
            edit={edit}
            os={data}
            itens={itens}
            onReloadItens={carregarItens}
            onChange={handleChangMovimentacoes}
          />
        </TabPanel>
        <TabPanel label="Execuções">
          <Execucoes os={data} />
        </TabPanel>
        <TabPanel label="Correções">
          <Correcoes os={data} />
        </TabPanel>
        <TabPanel label="Movimentações">
          <Movimentacoes
            edit={edit}
            idStatus={data.idStatus}
            idOrdemServico={id}
            consorcioEmpresa={data.contrato_consorcioEmpresa}
            idContrato={data.contrato_id}
            valorTotal={valorTotalOs}
            os={data}
            itens={itens}
            onChange={handleChangMovimentacoes}
          />
        </TabPanel>
        {mostrarAbaFiscalizacao && (
          <TabPanel label="Fiscalização">
            <Fiscalizacao
              os={data}
              idOrdemServico={id}
              itens={itens}
              onChange={handleChangMovimentacoes}
            />
          </TabPanel>
        )}
      </TabView>
    </Container>
  )
}
